import { useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { ChevronsUpDown, LogOut, Puzzle, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuItem, MenuSeparator, Tooltip } from '@strivelabs/ui-core/v2';
import { useSidebar } from '@strivelabs/ui-blocks/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { usePostHog } from 'posthog-js/react';
import { APP_ROUTES } from '@/constants';
import { useUserRole } from '@/hooks/useUserRole';
import { ANALYTICS_EVENTS, resetAnalytics, useTrack } from '@/lib/analytics';

/** Initials fallback when the user has no avatar. */
function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Footer identity row: avatar plus name, opening a menu with settings and sign
 * out. Collapses to the avatar alone in the rail, where the name has nowhere to
 * go.
 */
export function UserMenu() {
  const [open, setOpen] = useState(false);
  const { collapsed } = useSidebar();
  const { isGuest } = useUserRole();
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const trackEvent = useTrack();

  const handleSignOut = () => {
    trackEvent(ANALYTICS_EVENTS.USER_SIGNED_OUT, {});
    // Reset after the capture so the event still lands on the outgoing
    // person, and before sign-out so the next user on this browser starts
    // clean instead of inheriting the previous identity.
    resetAnalytics(posthog);
    return signOut({ redirectUrl: APP_ROUTES.SIGN_IN });
  };

  // Until Clerk resolves there is no user to name. Falling back to a literal
  // "Account" rendered a complete card — initials "A", and the presence dot
  // whose whole job is to report an active session — for someone who had not
  // been loaded, which is also exactly what an expired session looked like.
  if (!isLoaded) {
    return (
      <div
        aria-hidden
        className={cn(
          'animate-pulse bg-v2-sidebar-accent motion-reduce:animate-none',
          collapsed ? 'mx-auto size-9 rounded-v2-md' : 'h-11 w-full rounded-v2-xl',
        )}
      />
    );
  }

  const name = user?.fullName || user?.primaryEmailAddress?.emailAddress || 'Account';
  const email = user?.primaryEmailAddress?.emailAddress;

  // The presence dot is decorative: it reports that the session is active, which
  // the menu itself already implies, so it carries no accessible text.
  const avatar = (
    <span className="relative shrink-0">
      {user?.imageUrl ? (
        <img
          src={user.imageUrl}
          alt=""
          className="size-7 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
        />
      ) : (
        <span
          aria-hidden
          className="grid size-7 place-items-center rounded-full bg-v2-foreground text-v2-xs font-medium text-v2-background"
        >
          {initialsOf(name)}
        </span>
      )}
      <span
        aria-hidden
        className="absolute -bottom-px -right-px size-2.5 rounded-full bg-v2-success ring-2 ring-v2-background"
      />
    </span>
  );

  const trigger = (
    <button
      type="button"
      aria-label={`Account: ${name}`}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'flex items-center text-v2-base text-v2-sidebar-foreground',
        'transition-[background-color,transform] duration-150 hover:bg-v2-sidebar-accent active:scale-[0.96]',
        'motion-reduce:transition-none motion-reduce:active:scale-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
        // Collapsed: a clean centred icon button that matches the nav rail above.
        // Expanded: a raised card that reads as the one persistent footer control.
        collapsed
          ? 'mx-auto size-9 justify-center rounded-v2-md'
          : 'h-11 w-full gap-2.5 rounded-v2-xl border border-v2-sidebar-border bg-v2-background px-2.5 shadow-sm',
        open && 'bg-v2-sidebar-accent',
      )}
    >
      {avatar}
      {collapsed ? (
        null
      ) : (
        <>
          <span className="truncate">{name}</span>
          <ChevronsUpDown size={14} className="ml-auto shrink-0 opacity-50" />
        </>
      )}
    </button>
  );

  return (
    // `Menu`, not `Popover`: a Popover is an absolutely-positioned child of the
    // sidebar, so the rail's narrow box and its stacking context swallowed it.
    // `Menu` portals to the body and positions against the trigger, which is how
    // the design does it too — a fixed panel offset from the sidebar's edge.
    <Menu
      open={open}
      onOpenChange={setOpen}
      side="right"
      align="end"
      aria-label="Account menu"
      contentClassName={cn(collapsed ? 'w-[220px]' : 'w-[236px]', 'ring-offset-v2-sidebar')}
      trigger={collapsed ? <Tooltip label={name} side="right">{trigger}</Tooltip> : trigger}
    >
      {/* Identity header repeats the avatar so the menu stands on its own once
          it has left the trigger — in the rail the trigger is only an avatar. */}
      <div className="-mx-1 -mt-1 mb-1 flex items-center gap-2.5 border-b border-v2-border px-3 py-2.5">
        {avatar}
        <span className="min-w-0">
          <span className="block truncate text-v2-sm font-medium">{name}</span>
          {email ? (
            <span className="block truncate text-v2-xs text-v2-muted-foreground">{email}</span>
          ) : null}
        </span>
      </div>

      {/* Settings is not a route guests may visit, so offering it would send
          them straight back to Reports. Sign out is all they need here — and
          the only way they have of leaving, since their nav is Reports alone. */}
      {isGuest ? null : (
        <>
          <MenuItem label="Settings" onClick={() => navigate('/ui/settings/profile')}>
            <Settings size={15} className="shrink-0 opacity-70" />
            Settings
          </MenuItem>
          <MenuItem label="Apps & Extensions" onClick={() => navigate('/ui/settings/connections')}>
            <Puzzle size={15} className="shrink-0 opacity-70" />
            Apps & Extensions
          </MenuItem>
        </>
      )}
      {/* Sign out is destructive and irreversible in the session, so it sits
          below a rule rather than adjacent to a navigation item. */}
      {isGuest ? null : <MenuSeparator />}
      <MenuItem
        label="Sign out"
        destructive
        onClick={handleSignOut}
      >
        <LogOut size={15} className="shrink-0" />
        Sign out
      </MenuItem>
    </Menu>
  );
}
